import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Checkbox, FormControlLabel, TextField, Typography} from '@mui/material';
import {useDispatch} from 'react-redux';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import {useRequestProfileUpdateMutation} from '../../../../api/services/studentService';
import {showSnackbar} from '../../../../AppLayout/ApplicationSlice';

export const UPDATE_REQUEST_BUTTON = 'This information needs to be updated';
export const MAX_COMMENTS = 2000;

// AD-2827 and AD-2828 (Maria, 29 and 30 October 2026): a parent with View access tells the school that the information
// on the SEN(D) or Contacts tab needs to be updated. The school is sent an email; nothing on the profile changes.
export const canSendUpdateRequest = ({options, chosen, comments}) =>
    options?.length ? Boolean(chosen) : comments.trim().length > 0;

const UpdateRequestDialog = ({open, onClose, studentId, section, text, options}) => {
    const dispatch = useDispatch();
    const [chosen, setChosen] = useState(null);
    const [comments, setComments] = useState('');
    const [requestUpdate, {isLoading}] = useRequestProfileUpdateMutation();

    const close = () => {
        setChosen(null);
        setComments('');
        onClose();
    };

    const send = async () => {
        try {
            await requestUpdate({studentId, body: {section, chosenOption: chosen ?? '', comments}}).unwrap();
            dispatch(showSnackbar({message: 'Your request has been sent to the school', severity: 'success'}));
            close();
        } catch {
            dispatch(showSnackbar({message: 'Your request could not be sent. Please try again.', severity: 'error'}));
        }
    };

    return (
        <AdmicityDialog
            open={open}
            handleClose={close}
            title={UPDATE_REQUEST_BUTTON}
            actions={[
                {label: 'Cancel', onClick: close},
                {
                    label: 'Send',
                    variant: 'contained',
                    sx: {},
                    onClick: send,
                    disabled: isLoading || !canSendUpdateRequest({options, chosen, comments})
                }
            ]}
        >
            <Typography sx={{mb: 1.5}}>{text}</Typography>
            {options?.length > 0 && (
                <Box sx={{display: 'flex', flexDirection: 'column', mb: 1.5}}>
                    {options.map(option => (
                        <FormControlLabel
                            key={option}
                            sx={{alignItems: 'flex-start', mb: 0.5, '& .MuiCheckbox-root': {pt: 0.25}}}
                            control={<Checkbox checked={chosen === option} onChange={e => setChosen(e.target.checked ? option : null)}/>}
                            label={option}
                        />
                    ))}
                </Box>
            )}
            <TextField
                label="Comments"
                multiline
                minRows={3}
                fullWidth
                value={comments}
                onChange={e => setComments(e.target.value.slice(0, MAX_COMMENTS))}
            />
        </AdmicityDialog>
    );
};

UpdateRequestDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    studentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    section: PropTypes.oneOf(['Sen', 'Contacts']).isRequired,
    text: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string)
};

export default UpdateRequestDialog;
